<template>
  <section class="hero-outer py-4 sm:py-6" aria-labelledby="hero-heading">
    <div class="max-w-screen-lg mx-auto px-4">
      <div class="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4">

        <!-- Links: promotie-kaart -->
        <div class="hero-promo rounded-[12px] overflow-hidden relative min-h-[300px]">
          <!-- Achtergrond glow voor afbeelding -->
          <div class="absolute inset-0 flex items-center justify-end pr-6 pointer-events-none">
            <div class="w-[280px] h-[280px] rounded-full bg-white/20 blur-2xl"></div>
          </div>

          <div class="relative z-10 flex flex-col sm:flex-row items-stretch h-full">
            <!-- Tekst -->
            <div class="flex-1 p-6 sm:p-8 flex flex-col justify-center">
              <span class="inline-flex items-center gap-1.5 bg-black/10 text-[#111] text-[0.7rem] font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4 self-start">
                <i class="bi bi-battery-charging" aria-hidden="true"></i> Nieuw binnen
              </span>
              <h1 id="hero-heading" class="text-[clamp(1.5rem,3vw,2rem)] font-black leading-tight text-[#111] mb-1">
                Hoymiles HiBattery
              </h1>
              <p class="text-[#333] font-semibold text-[0.95rem] mb-3">1,92 kWh · Plug &amp; Play · IP66 · 10 jr garantie</p>
              <div class="flex items-baseline gap-2 mb-1">
                <span class="text-[#111] font-black text-[2rem]">€ 850,–</span>
                <span class="bg-black/10 text-[#111] text-[0.7rem] font-bold px-2 py-0.5 rounded-full">incl. btw</span>
              </div>
              <p class="text-[#444] text-[0.8rem] mb-4">€ 702,48 excl. btw</p>
              <ul class="list-none p-0 m-0 mb-5 flex flex-col gap-2">
                <li class="flex items-center gap-2 text-[#222] text-[0.85rem] font-medium">
                  <i class="bi bi-plug-fill text-[#111] text-[0.8rem] shrink-0" aria-hidden="true"></i>
                  Direct op stopcontact &ndash; geen installateur nodig
                </li>
                <li class="flex items-center gap-2 text-[#222] text-[0.85rem] font-medium">
                  <i class="bi bi-cpu-fill text-[#111] text-[0.8rem] shrink-0" aria-hidden="true"></i>
                  AI-sturing · 96,5% rendement · 800 VA
                </li>
                <li class="flex items-center gap-2 text-[#222] text-[0.85rem] font-medium">
                  <i class="bi bi-shield-fill-check text-[#111] text-[0.8rem] shrink-0" aria-hidden="true"></i>
                  ≥ 6.000 cycli LiFePO&#8324; · geschikt buiten (IP66)
                </li>
              </ul>
              <router-link
                to="/product/hoymiles-hibattery-1920-ac-sv"
                class="self-start inline-flex items-center gap-2 bg-[#111] text-white font-bold text-[0.875rem] px-5 py-2.5 rounded-[7px] no-underline hover:bg-[#333] transition-colors"
              >
                Bekijk HiBattery <i class="bi bi-arrow-right" aria-hidden="true"></i>
              </router-link>
            </div>

            <!-- Productafbeelding — wit vlak zodat donkere afbeelding zichtbaar is op geel -->
            <div class="hero-img-wrap relative hidden sm:flex items-center justify-center shrink-0 w-[260px]">
              <img
                :src="heroImg"
                alt="Hoymiles HiBattery thuisbatterij"
                class="relative z-10 max-h-[220px] max-w-[210px] object-contain drop-shadow-lg"
                width="210"
                height="220"
                loading="eager"
                fetchpriority="high"
                decoding="async"
                @error="(e) => e.target.style.display='none'"
              >
            </div>
          </div>
        </div>

        <!-- Rechts: USP panel -->
        <div class="usp-panel bg-[#fdf8ec] rounded-[12px] p-6 flex flex-col justify-between overflow-hidden">
          <div>
            <h2 class="text-[1rem] font-black text-[#111] mb-4">Waarom Plug2Charge?</h2>
            <ul class="list-none p-0 m-0 flex flex-col gap-3">
              <li class="flex items-center gap-2.5">
                <i class="bi bi-check-circle-fill text-[#FFD500] shrink-0"></i>
                <span class="text-[0.8rem] text-[#333] whitespace-nowrap">Voor 15:00 besteld, morgen in huis</span>
              </li>
              <li class="flex items-center gap-2.5">
                <i class="bi bi-check-circle-fill text-[#FFD500] shrink-0"></i>
                <span class="text-[0.8rem] text-[#333] whitespace-nowrap">Gratis verzending vanaf €50</span>
              </li>
              <li class="flex items-center gap-2.5">
                <i class="bi bi-check-circle-fill text-[#FFD500] shrink-0"></i>
                <span class="text-[0.8rem] text-[#333] whitespace-nowrap">2 jaar garantie op alle producten</span>
              </li>
              <li class="flex items-center gap-2.5">
                <i class="bi bi-check-circle-fill text-[#FFD500] shrink-0"></i>
                <span class="text-[0.8rem] text-[#333] whitespace-nowrap">30 dagen retour, niet goed geld terug</span>
              </li>
              <li class="flex items-center gap-2.5 hidden sm:flex">
                <i class="bi bi-check-circle-fill text-[#FFD500] shrink-0"></i>
                <span class="text-[0.8rem] text-[#333] whitespace-nowrap">A-merken uit eigen voorraad</span>
              </li>
            </ul>
          </div>

          <!-- Google rating -->
          <div class="mt-5 pt-4 border-t border-[#e8dfc0] flex items-center gap-3">
            <div class="w-8 h-8 shrink-0">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            </div>
            <div>
              <div class="flex items-center gap-1">
                <span class="text-[0.9rem] font-bold text-[#111]">4,9</span>
                <span class="flex text-[#FBBC05] text-[0.75rem]">
                  <i class="bi bi-star-fill"></i><i class="bi bi-star-fill"></i><i class="bi bi-star-fill"></i><i class="bi bi-star-fill"></i><i class="bi bi-star-fill"></i>
                </span>
              </div>
              <div class="text-[0.72rem] text-[#888]">Google beoordeling</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </section>
</template>

<script setup>
const heroImg = 'https://www.elektramat.nl/media/catalog/product/H/i/HiBattery-2-png_1.png'
</script>

<style scoped>
.hero-promo {
  background: linear-gradient(135deg, #FFD500 0%, #f0b800 100%);
}

@media (prefers-reduced-motion: no-preference) {
  .hero-promo { animation: fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both; }
  .usp-panel  { animation: fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) 0.1s both; }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
}

.price-bubble { animation: float 4s ease-in-out infinite; }
@keyframes float {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-6px); }
}
</style>
